import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiAward,
  FiCheck,
  FiClock,
  FiMapPin,
  FiShield,
  FiTarget,
  FiUsers,
  FiStar,
  FiEye,
  FiHeart,
  FiTrendingUp,
  FiPhone,
  FiMail,
  FiChevronRight,
  FiMenu,
  FiX,
} from "react-icons/fi";
import { pexelsImages } from "../assets/images/pexelsImages";
import logo from "../assets/melona logo.png";
import banner from "../assets/banner.jpg";

const VALUES = [
  { icon: <FiAward />, title: "Quality First", desc: "Every job is checked against material, color, finish, and handover standards." },
  { icon: <FiClock />, title: "Deadline Driven", desc: "Production is planned around your launch date, pickup time, and delivery needs." },
  { icon: <FiUsers />, title: "Client Partnership", desc: "We help clarify specs, prepare artwork, and keep communication practical." },
  { icon: <FiShield />, title: "Honest Pricing", desc: "Quotes are clear before printing starts, with no surprise finishing costs." },
];

const PROCESS = [
  { step: "01", title: "Artwork & File Check", desc: "We review your files for print-ready quality before production begins." },
  { step: "02", title: "Digital Proof Approval", desc: "You receive a digital preview to confirm layout, colors, and content." },
  { step: "03", title: "Material & Stock", desc: "Premium materials are selected and confirmed for your project." },
  { step: "04", title: "Quality Review", desc: "Every piece is inspected before handover to ensure perfection." },
];

const STATS = [
  { value: "10+", label: "Years Experience" },
  { value: "500+", label: "Projects Completed" },
  { value: "300+", label: "Happy Clients" },
  { value: "98%", label: "Satisfaction Rate" },
];

const AboutPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.title = "About Us - Melona Printing";
  }, []);

  return (
  <div className="melona-premium">
    {/* ── Sticky Navigation ── */}
    <nav className="melona-nav scrolled">
      <div className="melona-nav__inner">
        <Link to="/" className="melona-nav__logo">
          <img src={logo} alt="Melona" style={{ height: "40px" }} />
        </Link>
        <div className="melona-nav__links">
          <Link to="/">Home</Link>
          <Link to="/about" className="active">About</Link>
          <Link to="/services">Services</Link>
          <Link to="/#portfolio">Portfolio</Link>
          <Link to="/contact">Contact</Link>
          <Link to="/login">Login</Link>
        </div>
        <Link to="/contact" className="melona-nav__cta">
          <FiPhone /> Get Quote
        </Link>
        <button className="melona-nav__toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          {mobileMenuOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>
      
      {/* Mobile Menu Dropdown */}
      {mobileMenuOpen && (
        <div className="melona-mobile-menu">
          <nav>
            <Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link>
            <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="active">About</Link>
            <Link to="/services" onClick={() => setMobileMenuOpen(false)}>Services</Link>
            <Link to="/gallery" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
            <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Login</Link>
          </nav>
          <Link to="/contact" className="melona-btn melona-btn--primary melona-btn--full" style={{ marginTop: "var(--sp-4)" }} onClick={() => setMobileMenuOpen(false)}>
            <FiPhone /> Get Free Quote
          </Link>
        </div>
      )}
    </nav>

    {/* ── Hero ── */}
    <section className="melona-about-hero">
      <div className="melona-about-hero__bg">
        <img src={pexelsImages.hero} alt="Melona" />
        <div className="melona-about-hero__overlay" />
      </div>
      <div className="melona-container melona-about-hero__content">
        <span className="melona-eyebrow"><FiStar /> About Melona</span>
        <h1 className="melona-about-hero__title">
          Premium Print Work with a<br />
          <span className="melona-about-hero__title-accent">Clear Production Rhythm</span>
        </h1>
        <p className="melona-about-hero__subtitle">
          Melona is a Debre Birhan print studio for businesses that need sharp brand materials,
          dependable turnaround, and practical support — from quote to handover.
        </p>
        <div className="melona-about-hero__actions">
          <Link to="/contact" className="melona-btn melona-btn--primary">
            Start a Project <FiArrowRight />
          </Link>
          <Link to="/services" className="melona-btn melona-btn--outline">
            Explore Services
          </Link>
        </div>
        <div className="melona-about-hero__badge">
          <FiMapPin />
          <span>Debre Birhan, Atse Zeryakob Square, Kaleab Building</span>
        </div>
      </div>
    </section>

    {/* ── Stats Bar ── */}
    <section className="melona-stats-bar">
      <div className="melona-container">
        <div className="melona-stats-bar__grid">
          {STATS.map((stat, i) => (
            <div key={i} className="melona-stats-bar__item">
              <span className="melona-stats-bar__value">{stat.value}</span>
              <span className="melona-stats-bar__label">{stat.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Story Section ── */}
    <section className="melona-section">
      <div className="melona-container">
        <div className="melona-about-story">
          <div className="melona-about-story__image">
            <img src={banner} alt="Melona print finish" />
            <div className="melona-about-story__floating-card">
              <FiAward />
              <div>
                <strong>10+ Years</strong>
                <span>Trusted Experience</span>
              </div>
            </div>
          </div>
          <div className="melona-about-story__content">
            <span className="melona-eyebrow"><FiTarget /> Our Story</span>
            <h2 className="melona-section-title">Built for brands that want print to feel precise, not improvised.</h2>
            <p className="melona-about-story__text">
              Melona was founded by Mieraf Abiy to bring premium print quality, honest guidance,
              and a more organized production experience to local businesses in Debre Birhan.
            </p>
            <p className="melona-about-story__text">
              Today the studio supports business cards, labels, packaging, banners, brochures,
              and campaign materials with design help and pre-press checks — all under one roof.
            </p>
            <div className="melona-process-list">
              {PROCESS.map((p) => (
                <div key={p.step} className="melona-process-item">
                  <span className="melona-process-item__step">{p.step}</span>
                  <div>
                    <h4>{p.title}</h4>
                    <p>{p.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── Values Section ── */}
    <section className="melona-section melona-section--gray">
      <div className="melona-container">
        <div className="melona-section-header">
          <span className="melona-eyebrow"><FiHeart /> Our Values</span>
          <h2 className="melona-section-title">What customers can expect.</h2>
          <p className="melona-section-subtitle">Simple promises that make the print process easier to trust.</p>
        </div>
        <div className="melona-values-grid">
          {VALUES.map((value, i) => (
            <div key={i} className="melona-value-card">
              <div className="melona-value-card__icon">{value.icon}</div>
              <h3 className="melona-value-card__title">{value.title}</h3>
              <p className="melona-value-card__desc">{value.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── Owner / Founder Section ── */}
    <section className="melona-section">
      <div className="melona-container">
        <div className="melona-founder-card">
          <div className="melona-founder-card__avatar">MA</div>
          <div className="melona-founder-card__content">
            <span className="melona-eyebrow"><FiUsers /> Manager & Owner</span>
            <h2 className="melona-founder-card__name">Mieraf Abiy</h2>
            <p className="melona-founder-card__bio">
              Mieraf leads client relationships, scheduling, production quality, and team
              coordination so each order receives direct operational attention — from the first
              quote to the final handover.
            </p>
            <div className="melona-founder-card__contact">
              <Link to="/contact" className="melona-btn melona-btn--primary">
                Contact Melona <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ── CTA Strip ── */}
    <section className="melona-cta-strip">
      <div className="melona-container melona-cta-strip__inner">
        <div className="melona-cta-strip__icon"><FiTarget /></div>
        <div className="melona-cta-strip__text">
          <strong>Have an upcoming launch, event, or campaign?</strong>
          <span>Send the specs and Melona will help choose the right format and finish.</span>
        </div>
        <Link to="/contact" className="melona-btn melona-btn--gold">
          Get a Quote <FiArrowRight />
        </Link>
      </div>
    </section>


  </div>
  );
};

export default AboutPage;